/******************************************************************************
 * Memokid: A game to train one's memory                                      *
 * Copyright (C) 2022 Lukas Laag                                              *
 * This file is part of Memokid                                               *
 *                                                                            *
 * Memokid is free software: you can redistribute it and/or modify            *
 * it under the terms of the GNU General Public License as published by       *
 * the Free Software Foundation, either version 3 of the License, or          *
 * (at your option) any later version.                                        *
 *                                                                            *
 * Memokid is distributed in the hope that it will be useful,                 *
 * but WITHOUT ANY WARRANTY; without even the implied warranty of             *
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the              *
 * GNU General Public License for more details.                               *
 *                                                                            *
 * You should have received a copy of the GNU General Public License          *
 * along with Memokid.  If not, see <https://www.gnu.org/licenses/>.          *
 *                                                                            *
 ******************************************************************************/

import { go, routes } from '../location'
import nls from '../nls'
import getDecks from '../decks'
import { getRule } from '../stylesheets'
import { LEVELS, log } from '../logs'

export const START_GAME = 'START_GAME'
export const STOP_GAME = 'STOP_GAME'
export const ADD_FIELD_CARD = 'ADD_FIELD_CARD'
export const GRAB_NUPLET = 'GRAB_NUPLET'
export const TRANSFORM = 'TRANSFORM'
export const FLIP = 'FLIP'
export const CHANGE_PLAYER = 'CHANGE_PLAYER'
export const AI_UPDATE = 'AI_UPDATE'
export const SETTINGS_ADD_PLAYER = 'SETTINGS_ADD_PLAYER'
export const SETTINGS_REMOVE_PLAYER = 'SETTINGS_REMOVE_PLAYER'
export const SETTINGS_UPDATE_PLAYER = 'SETTINGS_UPDATE_PLAYER'
export const SETTINGS_CHANGE_DECK = 'SETTINGS_CHANGE_DECK'
export const SETTINGS_CHANGE_MATCH_COUNT = 'SETTINGS_CHANGE_MATCH_COUNT'
export const SETTINGS_CHANGE_CARD_COUNT = 'SETTINGS_CHANGE_CARD_COUNT'
export const SCORES_RESET = 'SCORES_RESET'
export const SCORES_ADD_GAME = 'SCORES_ADD_GAME'
export const LOAD_DECK = 'LOAD_DECK'

const startGame = (playerCards, mode) => ({ type: START_GAME, playerCards, mode })
const stopGame = () => ({ type: STOP_GAME })
const transform = (index, transform, animTransform) => ({ type: TRANSFORM, index, transform, animTransform })
const flip = (index, flipped, animFlipped) => ({ type: FLIP, index, flipped, animFlipped })
const addFieldCard = (fieldCard) => ({ type: ADD_FIELD_CARD, fieldCard })
const grabNuplet = (playerId, cardId, fieldCardIndices) => ({ type: GRAB_NUPLET, playerId, cardId, fieldCardIndices })
const changePlayer = (playerIndex) => ({ type: CHANGE_PLAYER, playerIndex })
const aiUpdate = (playerId, memory) => ({ type: AI_UPDATE, playerId, memory })
const loadDeck = (deckId, cards) => ({ type: LOAD_DECK, deckId, cards })
export const settingsAddPlayer = (player) => ({ type: SETTINGS_ADD_PLAYER, player })
export const settingsRemovePlayer = (playerId) => ({ type: SETTINGS_REMOVE_PLAYER, playerId })
export const settingsUpdatePlayer = (player) => ({ type: SETTINGS_UPDATE_PLAYER, player })
export const settingsChangeDeck = (deckRefid, cardCount) => ({ type: SETTINGS_CHANGE_DECK, deckRefid, cardCount })
export const settingsMatchCount = (matchCount) => ({ type: SETTINGS_CHANGE_MATCH_COUNT, matchCount })
export const settingsCardCount = (cardCount) => ({ type: SETTINGS_CHANGE_CARD_COUNT, cardCount })
export const scoresReset = () => ({ type: SCORES_RESET })
const scoresAddGame = (game) => ({ type: SCORES_ADD_GAME, game })

export const NO_ANIM = {
  timing: { duration: 0 }
}

export const PLAYER_TYPE_HUMAN = 'human'
export const PLAYER_TYPE_COMPUTER = 'computer'

const currentPlayers = (mode) => (player) => player.demo === (mode === 'demo')
const computerPlayers = (player) => player.type === PLAYER_TYPE_COMPUTER
export const demoPlayers = (player) => player.demo
export const nonDemoPlayers = (player) => !player.demo
export const filterPlayers = (players, predicate) =>
  players.filter((player) => predicate(player))

export const ensureLoaded = (deck, dispatch) => {
  if (!deck.loaded) {
    deck.loader((images) => {
      dispatch(
        loadDeck(
          deck.id,
          images.map((image, index) => ({ id: index, image }))
        )
      )
    })
  }
  return deck
}

export const defaultState = () => {
  const { intl } = nls
  return {
    decks: getDecks(),
    game: {
      mode: 'idle',
      fieldCards: [],
      currentPlayer: 0,
      playerCards: [
        {
          refid: 1,
          cardRefids: []
        },
        {
          refid: 2,
          cardRefids: []
        }
      ]
    },
    players: [
      {
        id: 1,
        name: intl.formatMessage({ id: 'game.demo' }, { id: 1 }),
        type: PLAYER_TYPE_COMPUTER,
        level: 3,
        demo: true
      },
      {
        id: 2,
        name: intl.formatMessage({ id: 'game.demo' }, { id: 2 }),
        type: PLAYER_TYPE_COMPUTER,
        level: 3,
        demo: true
      },
      {
        id: 3,
        name: intl.formatMessage({ id: 'game.player' }),
        type: PLAYER_TYPE_HUMAN,
        demo: false
      },
      {
        id: 4,
        name: intl.formatMessage({ id: 'game.computer' }),
        type: PLAYER_TYPE_COMPUTER,
        level: 3,
        demo: false
      }
    ],
    settings: {
      deckRefid: 0,
      matchCount: 2,
      cardCount: 16,
      playerSeq: 4
    },
    scores: [
    /*
      {
        date: 1484580516023,
        players: [player1, player2],
        scores: [12, 5]
      },
      {
        date: 1484580816023,
        players: [player1, player2],
        scores: [16, 1]
      }
    */
    ]
  }
}

export const toTransform = ({ x = 0, y = 0, r = 0, s = 1 }) =>
  ({ left: `${x}%`, top: `${y}%`, transform: `scale(${s}, ${s}) rotateZ(${r}deg)` })

export const toFlip = (flipped) =>
  ({ transform: `rotateY(${flipped ? 0 : 180}deg)` })

export const beginGame = (demo) => (dispatch, getState) => {
  const { decks, players, game: { mode }, settings: { deckRefid, matchCount, cardCount } } = getState()

  const initGame = () => {
    const deck = ensureLoaded(decks[deckRefid], dispatch)
    const cards = deck.cards
    const size = cardSize(matchCount, cardCount)
    writeCardSize(matchCount, cardCount)
    const center = 0.5 * (100 - size)
    const newMode = demo === true ? 'demo' : 'game'
    dispatch(
      startGame(
        filterPlayers(players, currentPlayers(newMode)).map(
          (player) => ({
            refid: player.id,
            cardRefids: [],
            memory: []
          })
        ),
        newMode
      )
    )
    dispatch(changePlayer(0))

    for (let i = 0; i < matchCount; i++) {
      cards.slice(0, cardCount).forEach((card, index) => {
        dispatch(addFieldCard({
          id: i * cardCount + index,
          refid: card.id,
          transform: { x: center, y: center, r: 0 },
          animTransform: NO_ANIM,
          flipped: false,
          animFlipped: NO_ANIM
        }))
      })
    }
  }

  const shuffle = () => {
    const newPos = () => ({
      x: (100 - 1.2 * size) * Math.random(),
      y: (100 - 1.2 * size) * Math.random()
    })
    const dist = (p1, p2) => {
      const dx = p2.x - p1.x
      const dy = p2.y - p1.y
      return dx * dx + dy * dy
    }
    const minDist = (p2) =>
      layout.reduce((minDist, p) => Math.min(minDist, dist(p, p2)), Number.MAX_VALUE)

    const size = cardSize(matchCount, cardCount)
    const layout = [newPos()]
    const { game: { fieldCards } } = getState()
    while (layout.length < fieldCards.length) {
      // For each card, attempt 10 random positions
      // and pick the one which is as far as possible from
      // the cards already layed out
      let pMax = null
      let dMax = 0
      for (let i = 0; i < 50; i++) {
        const p = newPos()
        const d = minDist(p)
        if (d > dMax) {
          pMax = p
          dMax = d
        }
      }
      layout.push(pMax)
    }
    let count = 0
    fieldCards.forEach((fieldCard, index) => {
      const newTransform = { ...layout[index], r: (Math.random() - 0.5) * 10 }
      const handleFinish = () => {
        log('game', LEVELS.DEBUG, 'Stopping transform', index)
        dispatch(
          transform(index, newTransform, NO_ANIM)
        )
        count++
        if (count === fieldCards.length &&
          aiPlaying(getState)) {
          // The computer begins
          dispatch(flipCard(aiNextMove(getState), true))
        }
      }
      const animTransform = {
        keyframes: [
          { offset: 0, ...toTransform(fieldCards[index].transform) },
          { offset: 1, ...toTransform(newTransform) }
        ],
        timing: {
          duration: 1500,
          easing: 'ease-in-out'
        },
        onFinish: handleFinish
      }

      dispatch(
        transform(index, newTransform, animTransform)
      )
    })
  }

  if (mode === 'idle') {
    log('game', LEVELS.INFO, 'newGame')
    initGame()
    shuffle()
  }
}

export const endGame = (demo) => (dispatch, getState) => {
  const { game: { mode } } = getState()
  if (mode !== 'idle') {
    log('game', LEVELS.INFO, 'endGame')
    dispatch(stopGame())
  }
}

const getPlayerCards = (playerCards, player) =>
  playerCards.find((obj) => obj.refid === player.id)

const cardSize = (matchCount, cardCount) => Math.sqrt(0.9 * 100 * 100 / (cardCount * matchCount)) *
  (window.innerWidth > window.innerHeight ? (window.innerHeight / window.innerWidth) : 1)

export const writeCardSize = (matchCount, cardCount) => {
  const cssRule = getRule('.card-size')
  if (cssRule) {
    cssRule.style.setProperty('width', `${cardSize(matchCount, cardCount)}%`)
  }
}

const animating = (fieldCards) =>
  fieldCards.find((card) => (card.animTransform !== NO_ANIM || card.animFlipped !== NO_ANIM)) != null

export const addPlayer = (player) => (dispatch, getState) => {
  const { settings: { playerSeq } } = getState()
  dispatch(settingsAddPlayer({ ...player, id: playerSeq + 1, demo: false }))
}

export const flipCard = (index, fromAi) => (dispatch, getState) => {
  const { game: { mode, fieldCards }, players, settings: { matchCount } } = getState()
  const players_ = filterPlayers(players, currentPlayers(mode))

  // Do not flip animating cards or cards already flipped
  // or anything while it is the computer's turn to play
  if ((!animating(fieldCards)) &&
      (!fieldCards[index].flipped) &&
      !(aiPlaying(getState) && !fromAi)) {
    const handleFinish = () => {
      log('game', LEVELS.DEBUG, 'Stopping flip', index)

      // Get updated state
      dispatch(
        flip(index, newFlipped, NO_ANIM)
      )
      // Update the AI if needed
      for (const player of players_.filter(computerPlayers)) {
        dispatch(
          aiUpdate(
            player.id,
            aiWatchMove(getState, player, index)))
      }

      // Compare number of flipped cards to nuplet size
      if (getState().game.fieldCards.filter((fieldCards) => fieldCards.flipped).length === matchCount) {
        setTimeout(
          () => dispatch(evaluate()),
          aiPlaying(getState) ? 1000 : 0
        )
      } else if (aiPlaying(getState)) {
        // The computer has not finished playing yet
        dispatch(flipCard(aiNextMove(getState), true))
      }
    }
    const newFlipped = !fieldCards[index].flipped
    const animFlipped = {
      keyframes: [
        { offset: 0, ...toFlip(!newFlipped) },
        { offset: 1, ...toFlip(newFlipped) }
      ],
      timing: {
        duration: 600
      },
      onFinish: handleFinish
    }

    log('game', LEVELS.DEBUG, 'Starting flip', index)
    dispatch(
      flip(index, newFlipped, animFlipped)
    )
  }
}

const evaluate = () => (dispatch, getState) => {
  const { game: { mode, fieldCards, currentPlayer }, settings: { matchCount }, players } = getState()
  if (mode !== 'idle') {
    const players_ = filterPlayers(players, currentPlayers(mode))

    // Compute the indices of the flipped cards
    const flipped = fieldCards.map((fieldCard, index) => index)
      .filter((index) => fieldCards[index].flipped)

    // From the flipped cards, compute a hash(image id, number of images with this id)
    const results = flipped.reduce((acc, index) => {
      const current = acc[fieldCards[index].refid]
      acc[fieldCards[index].refid] = current ? (current + 1) : 1
      return acc
    }, {})

    // Determine if a n-uplet has been found
    log('game', LEVELS.INFO, 'evaluate', results)
    const keys = Object.keys(results)
    if (keys.length === 1 && results[keys[0]] === matchCount) {
      log('game', LEVELS.INFO, 'grab')
      // Grab the n-uplet
      const barycenter = flipped.reduce((center, index) => {
        center.x += fieldCards[index].transform.x / flipped.length
        center.y += fieldCards[index].transform.y / flipped.length
        return center
      }, { x: 0, y: 0 })

      // Update the AI if needed
      for (const player of players_.filter(computerPlayers)) {
        dispatch(
          aiUpdate(
            player.id,
            aiGrab(getState, player, flipped)))
      }

      let count = 0
      flipped.forEach((index) => {
        const handleFinish = () => {
          log('game', LEVELS.DEBUG, 'Stopping grab')
          if (++count >= matchCount) {
            dispatch(
              grabNuplet(players_[currentPlayer].id, fieldCards[index].refid, flipped)
            )

            // last pair grabbed: game over
            if (fieldCards.length === matchCount) {
              gameOver(dispatch, getState)
            } else if (aiPlaying(getState)) {
              // The computer has not finished playing yet
              dispatch(flipCard(aiNextMove(getState), true))
            }
          }
        }
        const animTransform = {
          keyframes: [
            { offset: 0, ...toTransform(fieldCards[index].transform) },
            { offset: 0.6, ...toTransform(barycenter) },
            { offset: 1, ...toTransform({ ...barycenter, s: 0 }) }
          ],
          timing: {
            duration: 1500,
            easing: 'ease-in-out'
          },
          onFinish: handleFinish
        }
        dispatch(
          transform(index, { ...barycenter, s: 0 }, animTransform)
        )
      })
    } else {
      log('game', LEVELS.INFO, 'yield')
      // Flip cards back to yield to next player

      let count = 0
      flipped.forEach((index) => {
        const handleFinish = () => {
          log('game', LEVELS.DEBUG, 'Stopping yield')
          dispatch(
            flip(index, false, NO_ANIM)
          )
          if (++count >= matchCount) {
            const nextPlayer = (currentPlayer + 1) % players_.length
            dispatch(changePlayer(nextPlayer))
            if (players_[nextPlayer].type === PLAYER_TYPE_COMPUTER) {
              // Yield to the computer
              dispatch(flipCard(aiNextMove(getState), true))
            }
          }
        }
        const animFlipped = {
          keyframes: [
            { offset: 0, ...toFlip(true) },
            { offset: 1, ...toFlip(false) }
          ],
          timing: {
            duration: 600
          },
          onFinish: handleFinish
        }
        setTimeout(() => {
          dispatch(
            flip(index, false, animFlipped)
          )
        }, 1000)
      })
    }
  }
}

const AI = {
  1: {
    capacity: 4,
    forget: 'last'
  },
  2: {
    capacity: 5,
    forget: 'last'
  },
  3: {
    capacity: 6,
    forget: 'random'
  },
  4: {
    capacity: 6,
    forget: 'last'
  },
  5: {
    capacity: 8,
    forget: 'random'
  }
}

const memoryDump = (getState, memory) => {
  const cardAsString = (fieldCardId) => {
    const cardId = fieldCards.find((fieldCard) => fieldCard.id === fieldCardId).refid
    const card = cards.find((card) => card.id === cardId)
    return `${fieldCardId}:${card.image.substring(1 + card.image.lastIndexOf('/'))}`
  }
  const { decks, game: { fieldCards }, settings: { deckRefid } } = getState()
  const cards = decks[deckRefid].cards
  return memory.map((fieldCardId) => cardAsString(fieldCardId))
}

const aiNextMove = (getState) => {
  const { game: { mode, fieldCards, playerCards, currentPlayer }, players, settings: { matchCount } } = getState()
  const players_ = filterPlayers(players, currentPlayers(mode))
  const player = players_[currentPlayer]
  const memory = getPlayerCards(playerCards, player).memory
  const fieldCardsById = fieldCards.reduce((acc, fieldCard, index) => {
    acc[fieldCard.id] = { fieldCard, index }
    return acc
  }, {})
  log('game', LEVELS.INFO, 'aiNextMove', memoryDump(getState, memory))

  // From the memory, compute a hash(cardId, [fieldCardId])
  // keep those with enough cards to make a ply
  const results = Object.entries(
    memory.reduce((acc, fieldCardId) => {
      const cardId = fieldCardsById[fieldCardId].fieldCard.refid
      acc[cardId] = acc[cardId] ? [...acc[cardId], fieldCardId] : [fieldCardId]
      return acc
    }, {})
  ).filter((entry) => entry[1].length === matchCount)

  // Look for a field in the most promising (first) group which has
  // not been flipped yet
  if (results.length > 0) {
    const fieldCardIds = results[0][1].filter((fieldCardId) => !fieldCardsById[fieldCardId].fieldCard.flipped)
    if (fieldCardIds.length) {
      return fieldCardsById[fieldCardIds[0]].index
    }
  }
  // Otherwise pick a field card at random not already flipped or in the memory
  const candidates = Object.values(fieldCardsById).filter(({ fieldCard, index }) =>
    !fieldCard.flipped && memory.indexOf(fieldCard.id) === -1)
  return candidates[Math.floor(Math.random() * candidates.length)].index
}

const aiWatchMove = (getState, player, index) => {
  const { game } = getState()
  const fieldCardId = game.fieldCards[index].id
  const { capacity, forget } = AI[player.level]
  // Either the memory dumbly records the last flip
  // Or it first checks to see if did not already have that information

  const playerCards = getPlayerCards(game.playerCards, player)
  const memory = [...playerCards.memory]
  if (memory.indexOf(fieldCardId) === -1) {
    memory.push(fieldCardId)
  }
  // If the capacity is exceeded, either it forget one flip randomly
  // or it forgets the oldest flip
  if (memory.length > capacity) {
    memory.splice(forget === 'random' ? (Math.floor(Math.random() * memory.length)) : 0, 1)
  }
  log('game', LEVELS.INFO, 'aiWatchMove', memoryDump(getState, memory))
  return memory
}

const aiGrab = (getState, player, indices) => {
  const { game } = getState()
  const fieldCardIds = indices.map((index) => game.fieldCards[index].id)

  // Retain only the cards which have not been grabbed
  const playerCards = getPlayerCards(game.playerCards, player)
  log('game', LEVELS.INFO, 'aiGrab', playerCards.memory.filter(
    (fieldCardId) => fieldCardIds.indexOf(fieldCardId) === -1)
  )
  const memory = playerCards.memory.filter(
    (fieldCardId) => fieldCardIds.indexOf(fieldCardId) === -1)
  log('game', LEVELS.INFO, 'aiGrab', memoryDump(getState, memory))
  return memory
}

const aiPlaying = (getState) => {
  const { game: { mode, currentPlayer }, players } = getState()
  return filterPlayers(players, currentPlayers(mode))[currentPlayer].type === PLAYER_TYPE_COMPUTER
}

export const demoLeave = () => (dispatch, getState) => {
  const { game: { mode } } = getState()
  if (mode === 'demo') {
    dispatch(go(routes.HOME))
  }
}

const gameOver = (dispatch, getState) => {
  const { game: { mode, playerCards }, players } = getState()
  if (mode === 'game') {
    const players_ = filterPlayers(players, currentPlayers(mode))
    const game = {
      date: Date.now(),
      players: players_.map((player) => player.name),
      scores: players_.map((player) => getPlayerCards(playerCards, player).cardRefids.length)
    }
    dispatch(scoresAddGame(game))
  } else {
    log('game', LEVELS.INFO, 'demo end')
    dispatch(go(routes.HOME))
  }
}
